import { Phone, PhoneOff, Video } from 'lucide-react';
import type { PublicUser } from '../../types';

interface IncomingCallModalProps {
  user: PublicUser;
  onAccept: () => void;
  onDecline: () => void;
}

export default function IncomingCallModal({ user, onAccept, onDecline }: IncomingCallModalProps) {
  const initial = (user.name || user.email || '?').charAt(0).toUpperCase();
  const displayName = user.name || user.email || 'Friend';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Incoming video call"
    >
      <div className="relative max-w-sm w-full text-center space-y-8 p-8 rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-primary-500/30">
        <div className="absolute inset-0 rounded-2xl ring-4 ring-primary-500/40 animate-pulse pointer-events-none" />

        <div className="relative mx-auto w-28 h-28 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center overflow-hidden ring-4 ring-primary-500 ring-offset-4 ring-offset-white dark:ring-offset-gray-900">
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-4xl font-bold text-primary-600 dark:text-primary-300">{initial}</span>
          )}
        </div>

        <div className="relative space-y-1">
          <div className="flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400">
            <Video className="h-5 w-5 animate-pulse" />
            <p className="text-sm font-medium uppercase tracking-wide">Incoming video call</p>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Tap accept to join with camera and mic</p>
        </div>

        <div className="relative flex justify-center gap-10">
          <button
            type="button"
            onClick={onDecline}
            className="flex flex-col items-center gap-2 group"
            aria-label="Decline call"
          >
            <span className="w-16 h-16 rounded-full bg-red-500 group-hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors">
              <PhoneOff className="h-7 w-7" />
            </span>
            <span className="text-sm font-semibold text-red-500">Decline</span>
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="flex flex-col items-center gap-2 group"
            aria-label="Accept call"
          >
            <span className="w-16 h-16 rounded-full bg-green-500 group-hover:bg-green-600 text-white flex items-center justify-center shadow-lg animate-bounce transition-colors">
              <Phone className="h-7 w-7" />
            </span>
            <span className="text-sm font-semibold text-green-500">Accept</span>
          </button>
        </div>
      </div>
    </div>
  );
}
